import type { Student, ClassProfile, GameResult } from '../types';
import { formatDate, formatDateTime, downloadText } from './helpers';
import { namesToStudents } from './parser';
import { uuid } from './helpers';

export function exportStudentsCSV(students: Student[], className: string): void {
  const header = 'name,weight,pickCount,excluded,color,emoji';
  const rows = students.map((s) =>
    [s.name, s.weight, s.pickCount, s.excluded ? 1 : 0, s.color ?? '', s.emoji ?? ''].join(',')
  );
  const csv = [header, ...rows].join('\n');
  downloadText(csv, `굴려굴려뽑기_${className}_${formatDate(Date.now())}.csv`, 'text/csv');
}

export function exportResultTxt(result: GameResult, className: string): void {
  const lines: string[] = [];
  const ts = formatDateTime(result.timestamp);

  if (result.mode === 'single' || result.mode === 'multi' || result.mode === 'reward') {
    lines.push(`=== 굴려굴려뽑기 결과 (${ts}) ===`);
    lines.push(`학급: ${className}`);
    lines.push('');
    result.rankings
      .filter((r) => result.mode !== 'reward' || r.reward)
      .forEach((r) => lines.push(`${r.rank}등: ${r.name}${r.reward ? ` - ${r.reward}` : ''}`));
  } else if (result.mode === 'team' && result.teams) {
    lines.push(`=== 팀 나누기 결과 (${ts}) ===`);
    lines.push(`학급: ${className}`);
    lines.push('');
    result.teams.forEach((t) => lines.push(`${t.teamName}: ${t.members.join(', ')}`));
  }
  downloadText(lines.join('\n'), `굴려굴려뽑기_결과_${className}_${ts}.txt`);
}

export function exportResultCSV(result: GameResult, className: string): void {
  const ts = formatDateTime(result.timestamp);
  let csv = '';
  if (result.mode === 'team' && result.teams) {
    csv = '팀,멤버\n' + result.teams.map((t) => `${t.teamName},"${t.members.join(', ')}"`).join('\n');
  } else {
    csv = '순위,이름,보상\n' + result.rankings
      .filter((r) => result.mode !== 'reward' || r.reward)
      .map((r) => `${r.rank},${r.name},${r.reward ?? ''}`)
      .join('\n');
  }
  downloadText(csv, `굴려굴려뽑기_결과_${className}_${ts}.csv`, 'text/csv');
}

export function resultToText(result: GameResult): string {
  if (result.mode === 'team' && result.teams) {
    return result.teams.map((t) => `${t.teamName}: ${t.members.join(', ')}`).join('\n');
  }
  return result.rankings
    .filter((r) => result.mode !== 'reward' || r.reward)
    .map((r) => `${r.rank}등: ${r.name}${r.reward ? ` - ${r.reward}` : ''}`)
    .join('\n');
}

export function resultToMarkdown(result: GameResult): string {
  if (result.mode === 'team' && result.teams) {
    return result.teams.map((t) => `**${t.teamName}**: ${t.members.join(', ')}`).join('\n');
  }
  const emoji = ['🥇', '🥈', '🥉'];
  return result.rankings
    .filter((r) => result.mode !== 'reward' || r.reward)
    .map((r) => `${emoji[r.rank - 1] ?? `${r.rank}.`} ${r.name}${r.reward ? ` - ${r.reward}` : ''}`)
    .join('\n');
}

export async function importCSVAsProfile(file: File): Promise<ClassProfile | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = (e.target?.result as string).replace(/^﻿/, '');
        const lines = text.split('\n').filter(Boolean);
        if (lines.length < 2) {
          resolve(null);
          return;
        }
        const header = lines[0].toLowerCase().split(',');
        const nameIdx = header.indexOf('name');
        const weightIdx = header.indexOf('weight');
        const pickIdx = header.indexOf('pickcount');
        const excludedIdx = header.indexOf('excluded');
        const colorIdx = header.indexOf('color');
        const emojiIdx = header.indexOf('emoji');

        const isFullFormat = nameIdx !== -1;
        const students: Student[] = lines.slice(1).map((line) => {
          const cols = line.split(',');
          if (isFullFormat) {
            return {
              id: uuid(),
              name: cols[nameIdx]?.trim() ?? '',
              weight: parseFloat(cols[weightIdx]) || 1,
              pickCount: parseInt(cols[pickIdx]) || 0,
              excluded: cols[excludedIdx] === '1',
              color: cols[colorIdx]?.trim() || undefined,
              emoji: cols[emojiIdx]?.trim() || undefined,
            };
          } else {
            return { id: uuid(), name: cols[0]?.trim() ?? '', weight: 1, pickCount: 0, excluded: false };
          }
        }).filter((s) => s.name);

        const profileName = file.name.replace(/\.csv$/i, '').replace(/^굴려굴려뽑기_/, '').split('_')[0] || '새 학급';
        resolve({
          id: uuid(),
          name: profileName,
          students,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          teamHistory: [],
        });
      } catch {
        resolve(null);
      }
    };
    reader.readAsText(file, 'utf-8');
  });
}

export async function importSimpleNames(file: File): Promise<Student[]> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = (e.target?.result as string).replace(/^﻿/, '');
      const names = text.split('\n').map((l) => l.split(',')[0].trim()).filter(Boolean);
      resolve(namesToStudents(names));
    };
    reader.readAsText(file, 'utf-8');
  });
}
